import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { useQuery } from "react-query";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectGroup,
  SelectValue,
} from "@components/ui/select";
import { useMutation } from "react-query";
import { resetTcpAnomaly } from "@services/api/amlAnomalyDetection";
import { fetchDeviceProperties } from "@services/api/commonApis";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import { useState, useEffect } from "react";
import React from "react";
import { toast } from "@components/ui/use-toast";
import { Loader } from "lucide-react";
import CustomSelect from "@components/common/CustomSelect";

interface TcpResetProps {
  onClose: () => void;
  onSuccess?: () => void;
  show: boolean;
  data?: any;
}
interface FormData {
  eventName: string;
  sourceIp: string;
  sourcePort: string;
  destinationIp: string;
  destinationPort: string;
  digit?: string;
  durationUnit?: string;
  action?: string;
  timeUnit?: string;
  globalId?: string;
  anomEvtId?: number | string;
  type: string;
}

const durationOptions = [
  { value: "minute", label: "Minute" },
  { value: "hour", label: "Hour" },
];

const formFields = [
  // { key: "sourceIp", label: "Source Ip", placeholder: "Source IP" },
  // { key: "sourcePort", label: "Source Port", placeholder: "Source Port" },
  {
    key: "destinationIp",
    label: "Destination Ip",
    placeholder: "Destination IP",
  },
  {
    key: "destinationPort",
    label: "Destination Port",
    placeholder: "Destination Port",
  },
];
const ANY_OPTION = { label: "Any", value: "any" };
const TcpResetDialog: React.FC<TcpResetProps> = ({ onClose, show, data }) => {
  const [tcpResetForm, setTcpResetForm] = useState<FormData>({
    eventName: "",
    sourceIp: "",
    sourcePort: "",
    destinationIp: "",
    destinationPort: "",
    digit: "",
    action: "block",
    timeUnit: "minute",
    globalId: "",
    type: "tcpreset",
  });
  const [actionLoader, setActionLoader] = useState(false);
  const [eventOptions, setEventOptions] = useState<any[]>([]);
  const [sourceIpOptions, setSourceIpOptions] = useState<
    { label: string; value: string }[]
  >([ANY_OPTION]);
  const [sourcePortOptions, setSourcePortOptions] = useState<
    { label: string; value: string }[]
  >([ANY_OPTION]);

  const { data: deviceProperties, isLoading: devicePropertiesLoading } =
    useQuery(["fetchDeviceProperties"], () => fetchDeviceProperties());

  useEffect(() => {
    if (!data || !Array.isArray(data.eventData) || data.eventData.length === 0)
      return;

    const options = data.eventData.map((event: any) => {
      let parsedEventData: { data?: { sport?: string; dport?: string } } = {};
      try {
        parsedEventData =
          typeof event.EventData === "string"
            ? JSON.parse(event.EventData)
            : event.EventData || {};
      } catch (error) {
        console.error("Invalid JSON in EventData:", error);
      }
      return {
        label: `${event.EventName}`,
        value: event.EventId,
        event,
        parsedEventData,
      };
    });

    setEventOptions(options);
    const ips = options.map((opt: any) => opt.event?.SourceIp).filter(Boolean);
    const ports = options
      .map((opt: any) => opt.parsedEventData?.data?.sport)
      .filter(Boolean);

    const uniqueIps = Array.from(new Set(ips)) as string[];
    const uniquePorts = Array.from(new Set(ports)) as string[];

    setSourceIpOptions([
      ANY_OPTION,
      ...uniqueIps.map((ip) => ({ label: ip, value: ip })),
    ]);
    setSourcePortOptions([
      ANY_OPTION,
      ...uniquePorts.map((p) => ({ label: p, value: String(p) })),
    ]);

    if (options.length > 0) {
      const first = options[0];
      setTcpResetForm({
        eventName: first.value,
        sourceIp: first.event.SourceIp,
        sourcePort: first.parsedEventData.data?.sport || "",
        destinationIp: first.event.DestinationIp,
        destinationPort:
          first.parsedEventData.data?.dport ||
          first.parsedEventData.dport ||
          "",
        digit: "",
        action: "block",
        timeUnit: "minute",
        globalId: first.event.GlobalId,
        type: "tcpreset",
        anomEvtId: data?.anomaly_id,
      });
    }
  }, [data]);

  const updateField = (field: keyof FormData, value: string) => {
    setTcpResetForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEventSelect = (eventId: string) => {
    const selected = eventOptions.find((item) => item.value === eventId);
    if (selected) {
      setTcpResetForm((prev) => ({
        ...prev,
        eventName: selected.value,
        sourceIp: selected.event.SourceIp,
        sourcePort: selected.parsedEventData.data?.sport || "",
        destinationIp: selected.event.DestinationIp,
        destinationPort: selected.parsedEventData.data?.dport || "",
      }));
    }
  };

  const blockUnblockMutation = useMutation(resetTcpAnomaly, {
    onSuccess: () => {
      setTcpResetForm((prev) => ({
        ...prev,
        digit: "",
        action: "block",
        timeUnit: "minute",
      }));
      setActionLoader(false);
      toast({
        title: "Success",
        description: "TCP Reset action completed successfully",
        variant: "success",
      });
      onClose();
    },
    onError: () => {
      setActionLoader(false);
      toast({
        title: "Error",
        description: "An error occured!",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (
      !tcpResetForm.destinationIp ||
      !tcpResetForm.digit ||
      !tcpResetForm.timeUnit ||
      !tcpResetForm.action ||
      !tcpResetForm.sourceIp ||
      !tcpResetForm.sourcePort
    ) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields.",
        variant: "destructive",
      });
      return;
    }

    // Validate digit > 0 and numeric
    const durationValue = parseInt(tcpResetForm.digit, 10);
    if (isNaN(durationValue) || durationValue <= 0) {
      toast({
        title: "Validation Error",
        description: "Please select a valid duration.",
        variant: "destructive",
      });
      return;
    }
    setActionLoader(true);
    blockUnblockMutation.mutate({
      src_ip: tcpResetForm.sourceIp,
      dest_ip: tcpResetForm.destinationIp,
      src_port: tcpResetForm.sourcePort,
      dest_port: tcpResetForm.destinationPort,
      duration: tcpResetForm.digit,
      durationUnit: tcpResetForm.timeUnit,
      action: tcpResetForm.action,
      globalId: tcpResetForm.globalId,
      type: "tcpreset",
      anomEvtId: `event___${tcpResetForm.anomEvtId}`,
    });
  };

  return (
    <Dialog open={show} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[30rem]">
        <DialogHeader>
          <DialogTitle className="">
            <div>TCP RESET</div>
            <div className="text-xs">
              {!devicePropertiesLoading &&
                (deviceProperties?.tcp_reset_status?.enableTCPrst || 0) ==
                  0 && (
                  <div className="pt-1 flex gap-2">
                    <div className="text-primary flex-1">
                      Note: TCP Reset feature is currently disabled in device
                      settings.
                    </div>
                    <div>
                      <a
                        href="/settings/#license?widget=Allow TCP Reset"
                        className="text-primaryBlue underline"
                      >
                        Change Now
                      </a>
                    </div>
                  </div>
                )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="relative">
          <div className="space-y-4 p-2 gap-0">
            <div className="flex flex-col gap-2">
              <Label>Event</Label>
              <Select
                value={tcpResetForm.eventName}
                onValueChange={handleEventSelect}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Event" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {eventOptions.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label>Source IP</Label>
              <CustomSelect
                data={sourceIpOptions}
                defaultValue={
                  tcpResetForm.sourceIp ? [tcpResetForm.sourceIp] : []
                }
                onSelect={(value: any) =>
                  updateField(
                    "sourceIp",
                    Array.isArray(value) ? value[0] : value,
                  )
                }
                placeholder="Select Source IP"
                multiSelect={false}
              />
            </div>

            {/* Source Port */}
            <div className="flex flex-col gap-2">
              <Label>Source Port</Label>
              <CustomSelect
                data={sourcePortOptions}
                defaultValue={
                  tcpResetForm.sourcePort ? [tcpResetForm.sourcePort] : []
                }
                onSelect={(value: any) =>
                  updateField(
                    "sourcePort",
                    Array.isArray(value) ? value[0] : value,
                  )
                }
                placeholder="Select Source Port"
                multiSelect={false}
              />
            </div>
            {formFields.map((field) => (
              <div className="flex flex-col gap-2" key={field.key}>
                <Label className="">{field.label}</Label>
                <Input
                  placeholder={field.placeholder}
                  className="w-full"
                  value={tcpResetForm[field.key as keyof FormData] || ""}
                  onChange={(e) =>
                    updateField(field.key as keyof FormData, e.target.value)
                  }
                  disabled={
                    field.key === "destinationIp" ||
                    field.key === "destinationPort"
                  }
                />
              </div>
            ))}
            <div className="flex flex-col gap-2">
              <Label>Time out</Label>
              <div className="flex flex-row gap-2">
                <Input
                  className="w-full"
                  placeholder={
                    tcpResetForm.timeUnit === "hour"
                      ? "Enter hours"
                      : "Enter minutes"
                  }
                  value={tcpResetForm.digit || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      updateField("digit", value);
                    }
                  }}
                />

                <Select
                  value={tcpResetForm.timeUnit || ""}
                  onValueChange={(val) => updateField("timeUnit", val)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {durationOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label className="">Action</Label>
              <Select
                value={tcpResetForm.action}
                onValueChange={(val) => updateField("action", val)}
              >
                <SelectTrigger className="w-full ">
                  <SelectValue placeholder="Block" />
                </SelectTrigger>
                <SelectContent className="">
                  <SelectItem value="unblock">Unblock</SelectItem>
                  <SelectItem value="block">Block</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                onClick={onClose}
                className="bg-gray-600 text-white px-5 py-1 rounded"
                type="button"
              >
                Cancel
              </Button>
              {actionLoader ? (
                <Button type="button">
                  <Loader className="animate-spin" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} type="button">
                  Save
                </Button>
              )}
            </div>
          </div>
          {(devicePropertiesLoading ||
            (deviceProperties?.tcp_reset_status?.enableTCPrst || 0) == 0) && (
            <div className="absolute top-0 left-0 w-full h-full bg-black opacity-80 rounded-md shadow-xl flex items-center justify-center">
              {devicePropertiesLoading && (
                <Loader className="animate-spin w-12 h-12" />
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TcpResetDialog;
